import {
  createFrontendPlugin,
  PageBlueprint,
  PluginHeaderActionBlueprint,
  SubPageBlueprint,
} from '@backstage/frontend-plugin-api';
import type {
  ExtensionDefinition,
  OverridableFrontendPlugin,
} from '@backstage/frontend-plugin-api';
import {
  SearchFilterResultTypeBlueprint,
  SearchResultListItemBlueprint,
} from '@backstage/plugin-search-react/alpha';
import { RiFocus2Line } from '@remixicon/react';

import {
  rootRouteRef,
  overviewRouteRef,
  languagesAndFrameworksRouteRef,
  toolsRouteRef,
  platformsRouteRef,
} from './routes';

const techRadarPage = PageBlueprint.make({
  params: {
    path: '/tech-radar',
    routeRef: rootRouteRef,
    title: 'Tech Radar',
    icon: <RiFocus2Line />,
  },
});

// Sub-pages — each becomes a tab in the PluginHeader
const overviewSubPage = SubPageBlueprint.make({
  name: 'overview',
  params: {
    path: 'overview',
    title: 'Overview',
    routeRef: overviewRouteRef,
    loader: () =>
      import('./components/overview-page').then(m => <m.OverviewPage />),
  },
});

const languagesAndFrameworksSubPage = SubPageBlueprint.make({
  name: 'languages-and-frameworks',
  params: {
    path: 'languages-and-frameworks',
    title: 'Languages & Frameworks',
    routeRef: languagesAndFrameworksRouteRef,
    loader: () =>
      import('./components/category-subpage').then(m => (
        <m.CategorySubpage category="languages-and-frameworks" />
      )),
  },
});

const toolsSubPage = SubPageBlueprint.make({
  name: 'tools',
  params: {
    path: 'tools',
    title: 'Tools',
    routeRef: toolsRouteRef,
    loader: () =>
      import('./components/category-subpage').then(m => (
        <m.CategorySubpage category="tools" />
      )),
  },
});

const platformsSubPage = SubPageBlueprint.make({
  name: 'platforms',
  params: {
    path: 'platforms',
    title: 'Platforms',
    routeRef: platformsRouteRef,
    loader: () =>
      import('./components/category-subpage').then(m => (
        <m.CategorySubpage category="platforms" />
      )),
  },
});

export const headerActions = PluginHeaderActionBlueprint.make({
  params: defineParams =>
    defineParams({
      loader: () =>
        import('./components/header-actions').then(m => <m.HeaderActions />),
    }),
});

export const techRadarSearchFilterType: ExtensionDefinition =
  SearchFilterResultTypeBlueprint.make({
    name: 'tech-radar',
    params: {
      value: 'tech-radar',
      name: 'Tech Radar',
      icon: <RiFocus2Line />,
    },
  });

const techRadarSearchResultListItem = SearchResultListItemBlueprint.make({
  params: {
    predicate: result => result.type === 'tech-radar',
    component: () =>
      import('./components/tech-radar-search-result-list-item').then(
        m => m.TechRadarSearchResultListItem,
      ),
  },
});

export const techRadarPlugin: OverridableFrontendPlugin<{
  root: typeof rootRouteRef;
}> = createFrontendPlugin({
  pluginId: 'tech-radar',
  extensions: [
    techRadarPage,
    overviewSubPage,
    languagesAndFrameworksSubPage,
    toolsSubPage,
    platformsSubPage,
    headerActions,
    techRadarSearchFilterType,
    techRadarSearchResultListItem,
  ],
  routes: {
    root: rootRouteRef,
  },
});
